import { useEffect, useRef, useState } from 'react'
import { endpoints } from '@/services/endpoints'
import { createSharedSseConnection, sseEnabled } from '@/services/sse'
import { readingInstant } from '@/features/anomalies/api'

/**
 * A chart's LIVE readings — `GET /sensors/charts/{id}/stream`.
 *
 * ── What the server sends ────────────────────────────────────────────────
 * `event: reading`, data `{sensor_tag_id, timestamp, value, value_type}` — one
 * event per value of any tag on the chart, pushed from the Kafka consumer as it
 * lands. On connect the server first REPLAYS what it buffered for each tag over
 * the last `sensor_reading_buffer_window_s` (60s), so a fresh view is not empty.
 * `timestamp` is null when the source sent none; such a reading is placed at the
 * moment it arrived, which is the nearest honest instant we have.
 *
 * ── The tag set is fixed at connect ──────────────────────────────────────
 * The server subscribes the tags the chart held when the request arrived. So the
 * connection is keyed on the tag set and rebuilt when it changes — adding a tag
 * and still watching the old stream would show a series that never moves.
 *
 * ── Rendering is batched ─────────────────────────────────────────────────
 * Twenty tags at a few hertz is dozens of events a second. Each is pushed onto a
 * ref and the screen is repainted on a one-second tick, which is also what slides
 * the window forward when nothing is arriving at all.
 *
 * ── Reconnects ───────────────────────────────────────────────────────────
 * `createSharedSseConnection` reconnects with backoff, refreshes an expired token
 * once, and treats a 400 as final — which is what the per-user stream cap
 * (`max_concurrent_streams_per_user`) answers. A reconnect replays the server's
 * buffer again; readings are de-duplicated by tag and timestamp so that replay
 * does not double a trace.
 *
 * @param {string} chartId
 * @param {string[]} tagIds
 * @param {number} windowMs   How much recent history the view keeps.
 */
export function useSensorChartLive(chartId, tagIds, windowMs, { enabled = true } = {}) {
  const [status, setStatus] = useState('idle')
  /* Stamped with the connection it came from, so a snapshot left over from the
     previous tag set or chart reads as empty rather than being reset in the
     effect — which would be a synchronous second render on every change. */
  const [snapshot, setSnapshot] = useState(() => ({ key: null, readings: [], now: Date.now() }))
  const buffer = useRef([])
  const seen = useRef(new Set())
  const windowRef = useRef(windowMs)
  useEffect(() => {
    windowRef.current = windowMs
  }, [windowMs])

  const tagKey = [...tagIds].sort().join(',')
  const on = enabled && Boolean(chartId) && tagIds.length > 0 && sseEnabled()
  const connectionKey = on ? `${chartId}|${tagKey}` : null

  useEffect(() => {
    if (!connectionKey) return undefined
    buffer.current = []
    seen.current = new Set()

    const connection = createSharedSseConnection({
      path: endpoints.sensorCharts.stream(chartId),
      onStatus: setStatus,
      onEvent: (frame) => {
        if (frame?.event !== 'reading') return
        const value = Number(frame.value)
        if (!Number.isFinite(value) || !frame.sensor_tag_id) return
        const at = readingInstant(frame.timestamp) ?? new Date().toISOString()
        const key = `${frame.sensor_tag_id}|${at}`
        if (seen.current.has(key)) return
        seen.current.add(key)
        buffer.current.push({ tagId: frame.sensor_tag_id, at, value })
      },
    })
    const unsubscribe = connection.subscribe()

    const tick = setInterval(() => {
      const now = Date.now()
      const cutoff = now - windowRef.current
      // Trim what has slid out of view, and forget its de-duplication keys too,
      // or the set grows for as long as the tab is open.
      // Any reading, not just the first: a reconnect's replay arrives after newer ones.
      if (buffer.current.some((reading) => Date.parse(reading.at) < cutoff)) {
        const kept = buffer.current.filter((reading) => Date.parse(reading.at) >= cutoff)
        seen.current = new Set(kept.map((reading) => `${reading.tagId}|${reading.at}`))
        buffer.current = kept
      }
      setSnapshot({ key: connectionKey, readings: buffer.current.slice(), now })
    }, 1000)

    return () => {
      clearInterval(tick)
      unsubscribe()
    }
  }, [connectionKey, chartId])

  const current = snapshot.key === connectionKey
  return {
    readings: current ? snapshot.readings : EMPTY,
    span: { fromMs: snapshot.now - windowMs, toMs: snapshot.now },
    status: on && current ? status : on ? 'connecting' : 'idle',
    available: sseEnabled(),
  }
}

const EMPTY = []
